import { z } from "zod";
import type { TargetProfile } from "./schema";

// Structured extraction for a scraped job posting. Called ONCE per job at
// ingest, never per user — see plan/v4-job-pipeline-acquisition.md.
//
// The `.describe()` strings are load-bearing: with structured generation they
// are the model's real instructions. Edit them, not just the shapes.

export const SENIORITY_LEVELS = [
  "intern",
  "junior",
  "mid",
  "senior",
  "staff",
  "principal",
  "manager",
] as const;
export type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

export const WORK_MODES = ["onsite", "hybrid", "remote"] as const;

export const EMPLOYMENT_TYPES = [
  "full_time",
  "part_time",
  "contract",
  "internship",
  "temporary",
] as const;

export const SALARY_PERIODS = ["hour", "day", "month", "year"] as const;

// How usable the source text was. Aggregator feeds often truncate descriptions;
// "insufficient" means do not publish the page — the extraction is not trustworthy.
export const DESCRIPTION_QUALITY = ["full", "partial", "insufficient"] as const;

const skillSchema = z.object({
  name: z
    .string()
    .min(2)
    .max(60)
    .describe(
      "Canonical skill name, as a hiring manager would write it. Prefer the " +
        'conventional casing ("Kubernetes", "PostgreSQL", "CI/CD"). One skill ' +
        'per entry — never "React and Redux".',
    ),
  aliases: z
    .array(z.string().min(2).max(40))
    .max(6)
    .describe(
      "Other spellings the SAME skill appears under in a resume: abbreviations, " +
        'expansions, punctuation variants, common vendor names. "Kubernetes" -> ' +
        '["k8s", "EKS", "GKE"]. "Node.js" -> ["nodejs", "node js"]. ' +
        "CRITICAL: never a related or broader concept. Do NOT give " +
        '"engineer" for "software engineering", or "cloud" for "AWS". These are ' +
        "matched literally against resume text, so a broad alias creates a false " +
        "match and makes the score meaningless. Empty array is correct and " +
        "expected when the skill has no real synonym.",
    ),
});

const compensationSchema = z.object({
  min: z.number().nullable(),
  max: z.number().nullable(),
  currency: z.string().max(8).nullable().describe('ISO code where possible: "USD", "INR", "EUR".'),
  period: z.enum(SALARY_PERIODS).nullable(),
});

const redFlagSchema = z.object({
  label: z
    .string()
    .max(80)
    .describe(
      'Short neutral observation, not a judgement. "Asks for 6 years at a ' +
        'junior title", not "Bad company".',
    ),
  quote: z
    .string()
    .max(300)
    .describe(
      "The verbatim sentence from the posting that supports the label. " +
        "Required — if no sentence supports it, the flag must not be emitted.",
    ),
});

export const jobIntelLlmOutput = z.object({
  // --- Page identity -------------------------------------------------------
  title: z
    .string()
    .max(180)
    .describe("Cleaned job title, with req numbers and location suffixes stripped."),
  company: z.string().max(120).nullable(),

  oneLiner: z
    .string()
    .max(140)
    .describe(
      "One sentence for the social post and meta description. States who is " +
        "hiring, for what, and the single most distinctive detail. No hype, no " +
        'emoji, no "exciting opportunity".',
    ),

  summary: z
    .array(z.string().max(200))
    .min(3)
    .max(5)
    .describe(
      "The FIT JUDGEMENT, 3-5 lines — the one thing on the page a job board " +
        "does not give. Do NOT restate what the role does; `responsibilities` " +
        "already carries that, and repeating it wastes the whole field. Answer " +
        "instead: what level this really is versus what it is titled, who this " +
        "genuinely suits, who should not bother applying, and what is unusual " +
        "about it (an odd skill combination, a hard constraint, a mismatch " +
        "between the title and the ask). Be direct and specific. Say nothing " +
        "when there is nothing to say — a short honest summary beats a padded one.",
    ),

  responsibilities: z
    .array(z.string().max(160))
    .max(6)
    .describe(
      "The factual day-to-day only: what the person builds or runs, who they " +
        "report to, team size, on-call or travel expectations. No mission " +
        "statements, no company values, and no judgement — judgement belongs in " +
        "`summary`.",
    ),

  // --- Match inputs (load-bearing) -----------------------------------------
  mustHave: z
    .array(skillSchema)
    .max(15)
    .describe(
      "Skills that are genuinely required. A skill is must-have when it is " +
        "stated as required/must/essential, OR appears in the job title, OR is " +
        "the core technology the role is built around. When in doubt, put it in " +
        "niceToHave — a false must-have wrongly tells a good candidate not to apply.",
    ),
  niceToHave: z
    .array(skillSchema)
    .max(15)
    .describe(
      'Skills framed as "bonus", "a plus", "preferred", "familiarity with", or ' +
        "listed without being tied to a requirement.",
    ),

  seniority: z.enum(SENIORITY_LEVELS).nullable(),
  yearsMin: z.number().int().min(0).max(30).nullable(),
  yearsMax: z.number().int().min(0).max(30).nullable(),

  // --- Structured facts (also feed JobPosting schema.org markup) -----------
  locations: z
    .array(z.string().max(120))
    .max(8)
    .describe('As written in the posting. Empty when not stated — never guess "Remote".'),
  country: z
    .string()
    .length(2)
    .nullable()
    .describe(
      'ISO 3166-1 alpha-2 code for the primary work location: "IN", "US", "GB". ' +
        "This is the geography filter on the browse page, so it must come from " +
        "the posting, not from the company's headquarters or the currency. Null " +
        "when the posting names no country — including fully remote roles that " +
        "state no region.",
    ),
  workMode: z.enum(WORK_MODES).nullable(),
  employmentType: z.enum(EMPLOYMENT_TYPES).nullable(),
  compensation: compensationSchema
    .nullable()
    .describe("Null unless the posting states pay. Never estimate a market rate."),

  // --- Differentiators -----------------------------------------------------
  seniorityMismatch: z
    .string()
    .max(200)
    .nullable()
    .describe(
      "Set only when the stated level contradicts the stated experience — e.g. " +
        'a "Junior" title asking for 6 years. State the contradiction factually. ' +
        "Null when there is none, which is the normal case.",
    ),
  redFlags: z
    .array(redFlagSchema)
    .max(5)
    .describe(
      "Grounded observations a candidate would want before applying: unpaid " +
        "overtime hints, equity-only compensation, one role carrying several " +
        "jobs, an unreasonable requirement count. Empty array is the normal case " +
        "— do not manufacture flags to fill it.",
    ),

  // --- Self-report ---------------------------------------------------------
  descriptionQuality: z
    .enum(DESCRIPTION_QUALITY)
    .describe(
      'How complete the source text was. "partial" when it is visibly truncated ' +
        'or mostly boilerplate; "insufficient" when there is not enough to ' +
        "extract real requirements. Report this honestly — a page is not " +
        "published on an insufficient extraction.",
    ),
});

export type JobIntelLlmOutput = z.infer<typeof jobIntelLlmOutput>;

/**
 * The keyword hunt list for a job, as the match scorer sees it. The tailor and
 * the ATS booster derive their target list from here rather than from the
 * posting text, so coverage they report and the score the job page gives are
 * measured against one list, not two.
 *
 * soft_requirements is empty because intel does not model them — the rewrite
 * still fills that field itself.
 */
export function intelToTargetProfile(intel: JobIntelLlmOutput): TargetProfile {
  const keywords = [
    ...(intel.mustHave ?? []).map((s) => ({ canonical: s.name, tier: "core" as const })),
    ...(intel.niceToHave ?? []).map((s) => ({ canonical: s.name, tier: "secondary" as const })),
  ]
    // The posting states every one of these, so `from` is never "role_norm".
    .map((k) => ({ ...k, from: "jd" as const }))
    .slice(0, 40); // targetProfileSchema caps the array at 40
  return { keywords, soft_requirements: [] };
}
export type JobIntelSkill = z.infer<typeof skillSchema>;

// Post-generation guardrails. Zod cannot express these, and they are the checks
// that catch a regressed prompt or a changed source format.
const ALIAS_STOPWORDS = new Set([
  "and",
  "the",
  "with",
  "for",
  "in",
  "on",
  "of",
  "or",
  "to",
  "experience",
  "knowledge",
  "skills",
  "engineer",
  "developer",
  "senior",
  "junior",
  "strong",
  "good",
  "team",
  "work",
]);

export function validateJobIntel(intel: JobIntelLlmOutput): string[] {
  const problems: string[] = [];

  if (intel.descriptionQuality === "insufficient") {
    problems.push("descriptionQuality=insufficient");
  }
  if (intel.mustHave.length === 0) {
    problems.push("mustHave is empty");
  }

  for (const skill of [...intel.mustHave, ...intel.niceToHave]) {
    for (const alias of skill.aliases) {
      const normalized = alias.trim().toLowerCase();
      if (ALIAS_STOPWORDS.has(normalized)) {
        problems.push(`alias "${alias}" on "${skill.name}" is a stopword`);
      }
      if (normalized.split(/\s+/).length > 4) {
        problems.push(`alias "${alias}" on "${skill.name}" is a phrase, not a synonym`);
      }
    }
  }

  if (intel.yearsMin !== null && intel.yearsMax !== null && intel.yearsMin > intel.yearsMax) {
    problems.push("yearsMin > yearsMax");
  }

  const comp = intel.compensation;
  if (comp && comp.min !== null && comp.max !== null && comp.min > comp.max) {
    problems.push("compensation.min > compensation.max");
  }

  return problems;
}
